import React, { useEffect, useState } from "react";
import { api, openDocument } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FileText, Plus, Trash2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_DOC_TYPES = [
  { value: "gov_id", label: "Government ID" },
  { value: "income_proof", label: "Proof of Income" },
  { value: "paystub", label: "Pay Stub" },
  { value: "employment_verification", label: "Employment Verification" },
  { value: "benefit_letter", label: "Benefit Letter" },
  { value: "veteran_doc", label: "Veteran Documentation" },
  { value: "referral_letter", label: "Referral Letter" },
  { value: "housing_doc", label: "Housing Document" },
  { value: "facility_doc", label: "Facility / Program Document" },
  { value: "organization_doc", label: "Organization Document" },
  { value: "agency_record", label: "Agency Record" },
  { value: "other", label: "Other" },
];

export default function DocumentVault({
  title = "Document Vault",
  description = "Securely upload, view, and manage your documents.",
  docTypes = DEFAULT_DOC_TYPES,
  defaultLinkedTo = "",
  defaultLinkedId = "",
  linkedOptions = [
    { value: "none", label: "— Not linked —" },
    { value: "intake", label: "Housing Readiness / Intake" },
    { value: "applicant_verification", label: "Applicant Verification" },
    { value: "referral", label: "Referral" },
  ],
  testId = "document-vault",
}) {
  const [documents, setDocuments] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const initial = { name: "", document_type: docTypes[0].value, file: null, notes: "", expires_at: "", linked_to: defaultLinkedTo, linked_id: defaultLinkedId };
  const [form, setForm] = useState(initial);

  const load = async () => {
    const params = {};
    if (defaultLinkedTo) params.linked_to = defaultLinkedTo;
    if (defaultLinkedId) params.linked_id = defaultLinkedId;
    try {
      const { data } = await api.get("/documents/me", { params });
      setDocuments(data);
    } catch { setDocuments([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [defaultLinkedTo, defaultLinkedId]);

  const upload = async () => {
    if (!form.name || !form.file) { toast.error("Name and file are required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      fd.append("name", form.name);
      fd.append("document_type", form.document_type);
      if (form.notes) fd.append("notes", form.notes);
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      if (form.linked_to && form.linked_to !== "none") fd.append("linked_to", form.linked_to);
      if (form.linked_id) fd.append("linked_id", form.linked_id);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — pending admin review");
      setOpen(false);
      setForm(initial);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    load();
  };

  return (
    <div data-testid={testId}>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-[#0A192F]">{title}</h3>
          <p className="text-sm text-[#4A5568]">{description}</p>
        </div>
        <Button data-testid={`${testId}-add`} onClick={() => setOpen(true)} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white"><Plus className="w-4 h-4 mr-1.5" /> Upload Document</Button>
      </div>
      {documents.length === 0 ? (
        <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
          <FileText className="w-8 h-8 mx-auto text-[#4A5568] mb-3" />
          <div className="font-heading font-semibold text-[#0A192F]">No documents yet</div>
          <div className="text-sm text-[#4A5568] mt-1">Upload supporting documents to fast-track verification.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB]">
          {documents.map((d) => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="p-4 flex items-center gap-3 justify-between flex-wrap">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#1E3A8A]" />
                <div>
                  <div className="font-medium text-[#0A192F]">{d.name}</div>
                  <div className="text-xs text-[#4A5568]">
                    {docTypes.find((t) => t.value === d.document_type)?.label || d.document_type}
                    {d.original_filename && ` · ${d.original_filename}`}
                    {` · ${Math.round((d.size || 0) / 1024)} KB · Uploaded ${new Date(d.uploaded_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                {d.verified ? <Badge className="bg-[#C6F6D5] text-[#22543D]"><BadgeCheck className="w-3 h-3 mr-1" /> Verified</Badge> : <Badge variant="outline">Pending</Badge>}
                {d.rejection_reason && <Badge className="bg-[#FED7D7] text-[#742A2A] text-[10px]">Rejected</Badge>}
                {d.expires_at && <Badge variant="outline" className="text-[10px]">Exp {new Date(d.expires_at).toLocaleDateString()}</Badge>}
                {d.linked_to && <Badge variant="outline" className="text-[10px] capitalize">{d.linked_to.replace('_', ' ')}</Badge>}
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault();
                    try { await openDocument(d.id); }
                    catch (err) { toast.error(err.message); }
                  }}
                  className="text-xs text-[#1E3A8A] underline"
                  data-testid={`view-doc-${d.id}`}
                >View</a>
                <button onClick={() => remove(d.id)} data-testid={`del-doc-${d.id}`} className="p-1.5 hover:bg-[#F5F5F0] rounded"><Trash2 className="w-4 h-4 text-[#E53E3E]" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="block mb-1.5">Document name</Label><Input data-testid="doc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Driver's License" /></div>
            <div><Label className="block mb-1.5">Type</Label>
              <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{docTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="block mb-1.5">File (PDF, image, doc — max 25MB)</Label>
              <Input type="file" data-testid="doc-file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
              {form.file && <div className="text-xs text-[#4A5568] mt-1">{form.file.name} ({Math.round(form.file.size / 1024)} KB)</div>}
            </div>
            <div><Label className="block mb-1.5">Expiration date (optional)</Label>
              <Input type="date" data-testid="doc-expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            {!defaultLinkedTo && (
              <div><Label className="block mb-1.5">Link to (optional)</Label>
                <Select value={form.linked_to || "none"} onValueChange={(v) => setForm({ ...form, linked_to: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="doc-linked-to"><SelectValue placeholder="Not linked" /></SelectTrigger>
                  <SelectContent>{linkedOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="block mb-1.5">Notes (optional)</Label>
              <Textarea data-testid="doc-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="doc-upload" disabled={uploading} onClick={upload} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">{uploading ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
